"use client";

import { useEffect, useState } from "react";
import { useSafeAuth } from "@/hooks/use-safe-auth";
import { getMyStudentProfileForPrompt } from "@/app/profile/student/actions";

interface StudentProfilePrompt {
  /** 0–100 completion estimate (computed from the fetched student record —
   *  students have no backend completion endpoint like staff do). */
  percentage: number;
  /** A student registration record exists for this user. */
  hasProfile: boolean;
  /** Record exists but isn't approved yet → show the "complete / edit until
   *  approved" progress prompt (even at 100%, they can keep correcting). */
  needsCompletion: boolean;
  loading: boolean;
}

const filledStr = (v: any) => v != null && String(v).trim() !== "";

/** Same key-field set the student profile page uses for its progress bar. */
function computePct(p: any): number {
  const checks: boolean[] = [
    filledStr(p?.nameMyanmar), filledStr(p?.nameEnglish), filledStr(p?.gender), filledStr(p?.dateOfBirth),
    filledStr(p?.nrcNumber), filledStr(p?.placeOfBirth), filledStr(p?.race), filledStr(p?.religion),
    filledStr(p?.phoneNumber), filledStr(p?.email),
    filledStr(p?.stateRegionName), filledStr(p?.townshipName), filledStr(p?.permanentAddress), filledStr(p?.currentAddress),
    filledStr(p?.father?.nameEnglish), filledStr(p?.father?.nrcNumber),
    filledStr(p?.mother?.nameEnglish),
    filledStr(p?.guardian?.nameEnglish),
    Array.isArray(p?.batches) && p.batches.length > 0,
    Array.isArray(p?.previousEducation) && p.previousEducation.length > 0,
    filledStr(p?.profilePhoto),
  ];
  const filled = checks.filter(Boolean).length;
  return checks.length ? Math.round((filled / checks.length) * 100) : 0;
}

/**
 * Student mirror of `useStaffProfilePrompt`. Fetches the caller's own student
 * registration record once when authenticated; if one exists and isn't yet
 * approved, exposes a completion % + `needsCompletion` so the header dropdown
 * can show a "Complete / edit your profile — X%" progress prompt (like staff).
 * A user with no student record (staff/admin) gets `hasProfile:false` and the
 * prompt stays hidden.
 */
export function useStudentProfilePrompt(): StudentProfilePrompt {
  const { isAuthenticated, isLoading: authLoading } = useSafeAuth();
  const [percentage, setPercentage] = useState(0);
  const [hasProfile, setHasProfile] = useState(false);
  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await getMyStudentProfileForPrompt();
        if (cancelled) return;
        const p = r?.success ? (r.data as any) : null;
        if (p) {
          setHasProfile(true);
          setApproved(p.registrationStatus === "approved");
          setPercentage(computePct(p));
        }
      } catch {
        /* silent — no student record is a valid state (e.g. a staff user) */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  const needsCompletion = hasProfile && !approved;
  return { percentage, hasProfile, needsCompletion, loading };
}
